import React, { useState, useEffect, useCallback } from "react";
import { BookOpen, Check, X, Pencil, Trash2, Play, Pause, Loader } from "lucide-react";
import PageHeader from "./PageHeader";

const TARGET_LABELS = {
  aufgaben: "Aufgabenbeschreibungen",
  ton: "Ton & Bildhaftigkeit",
  sprache: "Sprache & Verständlichkeit",
  wirkung: "Bewerber-Nutzen & Wirkung",
  struktur: "Aufbau & Struktur",
  formregeln: "Formregeln",
  allgemein: "Allgemein",
};
const TARGET_KEYS = Object.keys(TARGET_LABELS);

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function RuleCard({ rule, onPatch, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(rule.regel);
  const [target, setTarget] = useState(rule.target);
  const [busy, setBusy] = useState(false);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const saveEdit = () =>
    run(async () => {
      await onPatch(rule.id, { regel: text.trim(), target });
      setEditing(false);
    });

  return (
    <div className={`rule-card card rule-status-${rule.status}`}>
      <div className="rule-card-top">
        <span className={`rule-badge rule-badge-${rule.target}`}>{TARGET_LABELS[rule.target] || rule.target}</span>
        <span className="rule-meta">{rule.created_by_email ? `${rule.created_by_email} · ` : ""}{formatDate(rule.created_at)}</span>
      </div>

      {editing ? (
        <div className="rule-edit">
          <textarea
            className="ai-prompt-input feedback-textarea"
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            autoFocus
          />
          <div className="rule-edit-row">
            <select className="sort-select" value={target} onChange={(e) => setTarget(e.target.value)}>
              {TARGET_KEYS.map((k) => <option key={k} value={k}>{TARGET_LABELS[k]}</option>)}
            </select>
            <div className="rule-actions">
              <button className="btn-ai-go" disabled={!text.trim() || busy} onClick={saveEdit}><Check size={13} /> Speichern</button>
              <button className="btn-ai-cancel" onClick={() => { setEditing(false); setText(rule.regel); setTarget(rule.target); }}>Abbrechen</button>
            </div>
          </div>
        </div>
      ) : (
        <p className="rule-text">{rule.regel}</p>
      )}

      {rule.beispiel_vorher && !editing && (
        <details className="rule-example">
          <summary>Auslösendes Beispiel</summary>
          <div className="rule-example-body">{rule.beispiel_vorher}</div>
        </details>
      )}

      {!editing && (
        <div className="rule-actions">
          {rule.status === "vorgeschlagen" && (
            <button className="btn-ai-go" disabled={busy} onClick={() => run(() => onPatch(rule.id, { status: "aktiv" }))}>
              {busy ? <Loader size={13} className="spin" /> : <Check size={13} />} Freigeben
            </button>
          )}
          {rule.status === "aktiv" && (
            <button className="btn-ai-auto" disabled={busy} onClick={() => run(() => onPatch(rule.id, { status: "abgelehnt" }))}>
              <Pause size={13} /> Deaktivieren
            </button>
          )}
          {rule.status === "abgelehnt" && (
            <button className="btn-ai-auto" disabled={busy} onClick={() => run(() => onPatch(rule.id, { status: "aktiv" }))}>
              <Play size={13} /> Aktivieren
            </button>
          )}
          <button className="btn-ai-auto" onClick={() => setEditing(true)}><Pencil size={13} /> Bearbeiten</button>
          {rule.status === "vorgeschlagen" && (
            <button className="btn-ai-cancel" disabled={busy} onClick={() => run(() => onPatch(rule.id, { status: "abgelehnt" }))}><X size={13} /> Ablehnen</button>
          )}
          <button className="btn-rule-del" disabled={busy} onClick={() => { if (window.confirm("Regel in den Papierkorb verschieben? Du kannst sie dort 30 Tage lang wiederherstellen.")) run(() => onDelete(rule.id)); }}>
            <Trash2 size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

export default function LearnedRulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    fetch("/api/redaktions-regeln", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => { setRules(Array.isArray(data.regeln) ? data.regeln : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const onPatch = async (id, body) => {
    const res = await fetch(`/api/redaktions-regeln/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) { alert("Fehler: " + (data.error || res.status)); return; }
    setRules((prev) => prev.map((r) => (r.id === id ? data.regel : r)));
  };

  const onDelete = async (id) => {
    const res = await fetch(`/api/redaktions-regeln/${id}`, { method: "DELETE", credentials: "include" });
    if (!res.ok) { alert("Löschen fehlgeschlagen"); return; }
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  const vorschlaege = rules.filter((r) => r.status === "vorgeschlagen");
  const aktiv = rules.filter((r) => r.status === "aktiv");
  const abgelehnt = rules.filter((r) => r.status === "abgelehnt");

  return (
    <>
      <PageHeader
        eyebrow="Künstliche Intelligenz"
        title="KI-Optimierungen"
        subtitle="Aus deinem Feedback destillierte Redaktionsregeln. Vorschläge prüfen und freigeben – aktive Regeln fließen bei jeder neuen Anzeige in die KI-Kette ein."
      />

      {loading ? (
        <div className="rule-empty"><Loader size={18} className="spin" /> Laden …</div>
      ) : rules.length === 0 ? (
        <div className="rule-empty">
          <BookOpen size={28} />
          <p>Noch keine Regeln gelernt.</p>
          <p className="rule-empty-hint">Gib bei einem Punkt einer Stellenanzeige über das Sprechblasen-Symbol Feedback – daraus entsteht hier ein Regel-Vorschlag.</p>
        </div>
      ) : (
        <div className="rule-sections">
          <section className="rule-section">
            <h3 className="rule-section-title">
              Vorschläge {vorschlaege.length > 0 && <span className="rule-count">{vorschlaege.length}</span>}
            </h3>
            {vorschlaege.length === 0 ? (
              <p className="rule-section-empty">Keine offenen Vorschläge.</p>
            ) : (
              vorschlaege.map((r) => <RuleCard key={r.id} rule={r} onPatch={onPatch} onDelete={onDelete} />)
            )}
          </section>

          <section className="rule-section">
            <h3 className="rule-section-title">
              Aktive Regeln {aktiv.length > 0 && <span className="rule-count rule-count-active">{aktiv.length}</span>}
            </h3>
            {aktiv.length === 0 ? (
              <p className="rule-section-empty">Noch keine aktiven Regeln.</p>
            ) : (
              aktiv.map((r) => <RuleCard key={r.id} rule={r} onPatch={onPatch} onDelete={onDelete} />)
            )}
          </section>

          {abgelehnt.length > 0 && (
            <section className="rule-section">
              <h3 className="rule-section-title rule-section-muted">Deaktiviert / Abgelehnt <span className="rule-count">{abgelehnt.length}</span></h3>
              {abgelehnt.map((r) => <RuleCard key={r.id} rule={r} onPatch={onPatch} onDelete={onDelete} />)}
            </section>
          )}
        </div>
      )}
    </>
  );
}
